import { ReactNode, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Certain from "../../../components/Operation/Certain";
import Logo from "../../../components/Operation/Logo";
import { openOpInfo } from "../../../components/Popup/PopupOpInfo";
import { selectOpMode, selectOverview } from "../../../util/store/selectors";
import { getStore } from "../../../util/store/store";
import { OPERATION_TYPE, serviceTask, VESSEL_TYPE } from "../../../util/varibles/constants";
import { getStartFinishByOpMode } from "../../../util/varibles/global";
import { IOperation, IPropsChildren, ISite, ITaskService, TOperation } from "../../../util/varibles/interface";
import { getTypesAndUnitOfTreatment } from "../../PlanOperationPage/util";
import Starving from "./Starving";
import styles from "./style.module.scss";
import useSiteLayout, { OPERATION_LANE_STEP, OPERATION_TOP_OFFSET } from "./useSiteLayout";

const descriptionView: { [id: string]: (data: TOperation, site: ISite) => string } = {
    [OPERATION_TYPE.SUPPORT]: () => "Support",
    [OPERATION_TYPE.SERVICE]: (operation) => {
        const { tasks } = operation;

        const taskTypes = tasks.reduce((rs: { [id: string]: string | undefined }, item: ITaskService) => {
            const { type = "" } = item;
            rs[type] = item.name
            return rs
        }, {})
        return Object.keys(taskTypes).map(key => {
            const name = taskTypes[key] || serviceTask[key || ""]?.name || " "
            return name;
        }).join(', ')
    },
    [OPERATION_TYPE.TRANSPORT]: (operation, site) => `(${operation.sub_operations.filter(item => item.site_id === site.id).map(item => item.unit_id).join(", ")})`,
    [OPERATION_TYPE.HARVEST]: (operation, site) => `(${operation.sub_operations.filter(item => item.site_id === site.id).map(item => item.unit_id).join(", ")})`,
    [OPERATION_TYPE.TREATMENT]: (operation) => {
        const { types } = getTypesAndUnitOfTreatment(operation.tasks);
        return `${Object.keys(types).map(k => types[k]).join(", ")} (${operation.sub_operations.map(item => item.unit_id).join(", ")})`
    },
}

interface IPropsOp extends IPropsChildren {
    site: ISite
    data: IOperation
    index: number
    height: number
}

const Operation: React.FC<IPropsOp> = ({ site, data, index, height }) => {
    const operation_mode = useSelector(selectOpMode);
    const { operation, activity_log } = data;
    const { status } = operation;
    const { start, finish } = getStartFinishByOpMode[operation_mode](activity_log, status);
    const [description, setDescription] = useState<ReactNode | undefined>();
    const [minWidth, setMinWidth] = useState<string | undefined>();
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const { [`${operation.operation_type}`]: descriptionFn } = descriptionView || {};
        const description = descriptionFn?.(operation, site);
        setDescription(description);
        setTimeout(() => {
            if (rootRef.current) {
                const { childNodes } = rootRef.current;
                let minWidth = 12;
                childNodes.forEach(element => {
                    const { offsetWidth } = element as HTMLElement;
                    minWidth += (offsetWidth + 5);
                });
                setMinWidth(`${minWidth}px`)
            }
        }, 10)
    }, [operation, site])

    const style: any = {
        '--op-left': start,
        '--op-width': finish - start,
        '--op-min-width': minWidth,
        top: `${index * OPERATION_LANE_STEP + OPERATION_TOP_OFFSET - height}px`
    }

    const handleClick = () => {
        const { related_id: mainId = "", vessel_id, vessel_name, vessel_type = VESSEL_TYPE.PROCESS_VESSEL } = data.operation;
        let spOps: IOperation[] = [];
        if (mainId.length > 0) {
            const operations = selectOverview.opsSite(getStore())
            spOps = Object.keys(operations).reduce((rs: IOperation[], key) => {
                const { operation_type, related_id } = operations[key].operation;
                if (operation_type === OPERATION_TYPE.SUPPORT && related_id === mainId) {
                    return [...rs, operations[key]]
                }
                return rs;
            }, [])
        }
        openOpInfo({ isEdit: false, data: { ...data, vessel: { ...data.vessel || {}, id: vessel_id || "", name: vessel_name || "", type: vessel_type } }, spOps })
    }

    return <div ref={rootRef} className={styles.operation} style={style} onClick={handleClick}>
        <Logo type={operation.operation_type} status={status} />
        <Certain operation={operation} status={status} isLabel={false} />
        <div className="flex items-center ml-[5px] !text-[14px]">
            <span className="whitespace-nowrap">{data.operation.vessel_name}</span>
            <span className="mx-1"> - </span>
            <span className="whitespace-nowrap">{description}</span>
        </div>
    </div>
}

const Site: React.FC<{ site: ISite, lanes: IOperation[][], height: number, bottom: number }> = ({ site, lanes, height, bottom }) => {
    return <div className={styles.siteOp} style={{ "--site-bottom": `${bottom}px` } as React.CSSProperties}>
        <Starving site={site} height={height} />
        {lanes.map((operations, laneIndex) => operations.map(item =>
            <Operation key={item.operation.id} data={item} site={site} index={laneIndex} height={height} />
        ))}
    </div>
}

const Operations: React.FC = () => {
    const layout = useSiteLayout();

    return <>
        {layout.map(({ site, lanes, height, bottom }) => <Site key={site.id} site={site} lanes={lanes} height={height} bottom={bottom} />)}
    </>
}

export default Operations;
